import { ref, computed } from 'vue'
import { localLabs, shouldLoadLocalLabs } from '../labs.config.js'

// Global state for eligible labs (shared across all component instances)
const eligibleLabs = ref([])
const labsLoaded = ref(false)

export default function useEnrolledLabs () {
  const { $res } = useNuxtApp()

  /**
   * Fetch eligible labs from the API
   * Only fetches once per session
   */
  const fetchEligibleLabs = async () => {
    if (labsLoaded.value) {
      return eligibleLabs.value
    }

    try {
      const labs = await $res.labs.eligibleLabs()
      let mergedLabs = labs && Array.isArray(labs) ? labs : []

      // In non-production environments, merge local labs
      if (shouldLoadLocalLabs()) {
        const enabledLocalLabs = localLabs.filter((lab) => lab.enabled !== false)

        // Add local labs that don't conflict with API labs
        enabledLocalLabs.forEach((localLab, index) => {
          const existsInApi = mergedLabs.some(
            (apiLab) => (localLab?.labPath && apiLab.labPath === localLab.labPath) || (localLab?.labComponent && apiLab.labComponent === localLab.labComponent)
          )

          if (!existsInApi) {
            mergedLabs.push({
              ...localLab,
              labExperimentId: 9001 + index // Dynamically assign high experiment ID
            })
            console.log(`[Labs Nav] Loaded local lab: ${localLab.name}`)
          } else {
            console.log(`[Labs Nav] Skipped local lab "${localLab.name}" (exists in API)`)
          }
        })
      }

      eligibleLabs.value = mergedLabs
      labsLoaded.value = true
    } catch (error) {
      console.error('[Labs Nav] Failed to fetch eligible labs:', error)
      labsLoaded.value = true
    }

    return eligibleLabs.value
  }

  /**
   * Force refresh eligible labs from the API
   * Use this after enrollment changes to update badges
   */
  const refreshEligibleLabs = async () => {
    labsLoaded.value = false
    return await fetchEligibleLabs()
  }

  const redirectsMap = computed(() => {
    return eligibleLabs.value.reduce((acc, lab) => {
      if (lab?.labPath && lab?.replacesPath) {
        acc[lab.replacesPath] = lab.labPath
      }

      return acc
    }, {})
  })

  /**
   * Get the redirect path for a given navigation link
   * Returns the lab path if the user is enrolled in a lab that replaces this path
   * Otherwise returns the original link
   *
   * @param {string} originalLink - The original navigation link path
   * @returns {string} - The lab path if enrolled, otherwise the original link
   */
  const getRedirectPath = (path) => redirectsMap.value[path] || path

  /**
   * Check if a given path is replaced by a lab experiment
   *
   * @param {string} path - The path to check
   * @returns {boolean} - True if the path is replaced by a lab
   */
  const isPathReplacedByLab = (path) => Object.hasOwn(redirectsMap.value, path)

  // Computed property to get all enrolled labs
  const enrolledLabs = computed(() => eligibleLabs.value || [])

  const getLabByComponent = (comp) => enrolledLabs.value.find((lab) => lab?.labComponent === comp)

  const getLabByRoute = (route) => enrolledLabs.value.find((lab) => lab?.labPath === route)

  const unenroll = async (id, callback) => {
    const res = await $res.labs.declineLabForUser(id)

    if (res) {
      await refreshEligibleLabs()
    }

    return res
  }

  return {
    fetchEligibleLabs,
    refreshEligibleLabs,
    getRedirectPath,
    isPathReplacedByLab,
    enrolledLabs,
    labsLoaded,
    getLabByComponent,
    getLabByRoute,
    unenroll
  }
}
